import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';

const T = {
  bg: "#2C2E33", txt: "#FAFAFA", tx3: "#9CA3AF", blue: "#4059AD",
  bdr: "rgba(217,219,221,0.12)", sf: "rgba(255,255,255,0.06)",
};

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "done", label: "Done" },
];
const TASK_TYPES = [
  { value: "", label: "—" },
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
  { value: "follow_up", label: "Follow-up" },
  { value: "admin", label: "Admin" },
  { value: "other", label: "Other" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function TaskModal({ open, mode, task, onClose, onSave }) {
  const store = useStore();
  const auth = useAuth();
  const titleRef = useRef(null);
  const popupRef = useRef(null);
  const [closing, setClosing] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [form, setForm] = useState(() => defaultForm(auth));

  function defaultForm(a) {
    return {
      title: "", description: "", pri: "medium", status: "pending",
      taskType: "", due: today(), ct: "", deal: null,
      assignee: a?.currentUser?._dbId || "",
    };
  }

  // Reset form whenever the modal opens or the task under edit changes.
  useEffect(() => {
    if (!open) return;
    setAttempted(false);
    setClosing(false);
    if (mode === "edit" && task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        pri: task.pri || task.priority || "medium",
        status: task.status || (task.done ? "done" : "pending"),
        taskType: task.taskType || task.task_type || "",
        due: task.due || task.due_date || today(),
        ct: task.ct || "",
        deal: task.deal || null,
        assignee: task.assignee || task.assigned_to || auth?.currentUser?._dbId || "",
      });
    } else {
      setForm(defaultForm(auth));
    }
    // Focus title on next tick.
    setTimeout(() => { titleRef.current?.focus(); }, 50);
  }, [open, mode, task]);

  // Escape closes; Enter (outside textarea) saves.
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "Enter" && e.target?.tagName !== "TEXTAREA") {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, form]);

  if (!open) return null;

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => { onClose?.(); setClosing(false); }, 180);
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      setAttempted(true);
      titleRef.current?.focus();
      return;
    }
    onSave?.(form);
  };

  const inpS = {
    padding: "7px 10px", borderRadius: 8,
    border: "1px solid rgba(217,219,221,0.12)",
    background: "rgba(255,255,255,0.05)",
    color: "#FAFAFA", fontSize: 12, outline: "none",
    fontFamily: "inherit", width: "100%", boxSizing: "border-box",
  };
  const titleInpS = {
    ...inpS,
    border: attempted && !form.title.trim()
      ? "1px solid #E53935"
      : "1px solid rgba(217,219,221,0.12)",
  };
  const labelS = {
    fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em",
    textTransform: "uppercase", marginBottom: 4, display: "block",
  };

  const contacts = store.contacts || [];
  const teamMembers = store.teamMembers || [];

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @keyframes tmIn{from{opacity:0;transform:scale(0.92) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes tmOut{from{opacity:1;transform:scale(1) translateY(0)}to{opacity:0;transform:scale(0.95) translateY(4px)}}
        @keyframes tmBgIn{from{opacity:0}to{opacity:1}}
        @keyframes tmBgOut{from{opacity:1}to{opacity:0}}
        .tm-input:focus { border-color: #4059AD !important; box-shadow: 0 0 0 3px rgba(64,89,173,0.15) !important; }
        .tm-input::placeholder { color: rgba(250,250,250,0.3) !important; }
        input[type="date"].tm-input { color-scheme: dark; }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(4px)",
          animation: closing ? "tmBgOut .18s ease forwards" : "tmBgIn .2s ease",
        }}
      />

      {/* Card */}
      <div
        ref={popupRef}
        style={{
          position: "relative",
          width: 440, maxWidth: "92vw", padding: "22px 24px",
          background: "#33363D", borderRadius: 18,
          border: "1px solid rgba(217,219,221,0.12)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.50), 0 0 0 1px rgba(217,219,221,0.05)",
          animation: closing ? "tmOut .18s ease forwards" : "tmIn .25s cubic-bezier(.16,1,.3,1)",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.txt, letterSpacing: ".04em", textTransform: "uppercase" }}>
            {mode === "edit" ? "Edit Task" : "New Task"}
          </div>
          <button
            onClick={handleClose}
            style={{
              width: 24, height: 24, borderRadius: 7, border: "1px solid rgba(217,219,221,0.10)",
              background: "rgba(255,255,255,0.04)", color: T.tx3, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
              fontFamily: "inherit",
            }}
          >×</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Title */}
          <div>
            <label style={labelS}>Title *</label>
            <input
              ref={titleRef}
              className="tm-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="What needs to be done?"
              style={titleInpS}
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelS}>Description</label>
            <textarea
              className="tm-input"
              rows={3}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Add notes, context, links…"
              style={{ ...inpS, resize: "vertical", minHeight: 64 }}
            />
          </div>

          {/* Priority + Status */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelS}>Priority</label>
              <select
                className="tm-input"
                value={form.pri}
                onChange={e => setForm(f => ({ ...f, pri: e.target.value }))}
                style={inpS}
              >
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelS}>Status</label>
              <select
                className="tm-input"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                style={inpS}
              >
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Due date + Task type */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelS}>Due date</label>
              <input
                className="tm-input"
                type="date"
                value={form.due || ""}
                onChange={e => setForm(f => ({ ...f, due: e.target.value }))}
                style={inpS}
              />
            </div>
            <div>
              <label style={labelS}>Task type</label>
              <select
                className="tm-input"
                value={form.taskType || ""}
                onChange={e => setForm(f => ({ ...f, taskType: e.target.value }))}
                style={inpS}
              >
                {TASK_TYPES.map(t => <option key={t.value || "_none"} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Contact */}
          <div>
            <label style={labelS}>Contact</label>
            <select
              className="tm-input"
              value={form.ct || ""}
              onChange={e => setForm(f => ({ ...f, ct: e.target.value }))}
              style={inpS}
            >
              <option value="">— Optional —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {(c.fn || "") + " " + (c.ln || "")}{c.co ? " (" + c.co + ")" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Assignee */}
          <div>
            <label style={labelS}>Assignee *</label>
            <select
              className="tm-input"
              value={form.assignee || ""}
              onChange={e => setForm(f => ({ ...f, assignee: e.target.value }))}
              style={inpS}
            >
              <option value="">— Select —</option>
              {teamMembers.map(m => (
                <option key={m._dbId || m.id} value={m._dbId || ""}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
          <button
            onClick={handleClose}
            style={{
              padding: "8px 18px", borderRadius: 10, border: "1px solid " + T.bdr,
              background: "transparent", color: T.tx3, fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >Cancel</button>
          <button
            onClick={handleSave}
            style={{
              padding: "8px 18px", borderRadius: 10, border: "none",
              background: T.blue, color: "#FAFAFA", fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
              boxShadow: "0 2px 12px rgba(64,89,173,0.30)",
            }}
          >{mode === "edit" ? "Save" : "Create Task"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
